import * as models from "../lib/api/models";
import ExpandableInputGroup from "./ExpandableInputGroup";
import getulFullName from "../lib/getFullName";
import LinedInput, {InputType} from "./LinedInput";
import React, {useRef} from "react";
import useFocusInvalid from "../lib/useFocusInvalid";
import validateField from "../lib/validateApplicationField";

const strings = {
    firstName: "First Name",
    invalidName: "Name is invalid",
    lastName: "Last Name",
    middleInitial: "Middle Initial",
    name: "Name",
};

type Props = {
    firstName: string;
    isExpanded: boolean;
    lastName: string;
    middleInitial: string;
    onBlurInput: (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onChangeName: (name: models.AppKeys, value: string) => void;
    setIsExpanded: (expanded: boolean) => void;
    firstInvalidKey?: string;
    validationFirst?: string[];
    validationMiddle?: string[];
    validationLast?: string[];
    validationCount: number;
};

function isNameValid(firstName: string, middleInitial: string, lastName: string): boolean {
    return (
        validateField(models.AppKeys.FirstName, firstName).isValid &&
        validateField(models.AppKeys.MiddleInitial, middleInitial).isValid &&
        validateField(models.AppKeys.LastName, lastName).isValid
    );
}

export default function ExpandableName(props: Props) {
    const hasNameValidationMsg =
        props.validationFirst || props.validationMiddle || props.validationLast;
    const validationMessage = hasNameValidationMsg ? [strings.invalidName] : undefined;

    const isValid =
        !hasNameValidationMsg && isNameValid(props.firstName, props.middleInitial, props.lastName);

    const groupValue = getulFullName(props.firstName, props.middleInitial, props.lastName);

    const firstRef = useRef<HTMLInputElement>(null);
    const middleRef = useRef<HTMLInputElement>(null);
    const lastRef = useRef<HTMLInputElement>(null);

    let invalidRef;
    if (props.firstInvalidKey === models.AppKeys.FirstName) {
        invalidRef = firstRef;
    } else if (props.firstInvalidKey === models.AppKeys.MiddleInitial) {
        invalidRef = middleRef;
    } else if (props.firstInvalidKey === models.AppKeys.LastName) {
        invalidRef = lastRef;
    }

    useFocusInvalid(props.validationCount, invalidRef?.current);

    return (
        <ExpandableInputGroup
            isExpanded={props.isExpanded}
            isValid={isValid}
            name={strings.name}
            setIsExpanded={props.setIsExpanded}
            validationMessage={validationMessage}
            value={groupValue}
            focusFirst={() => firstRef?.current?.focus()}
        >
            {({onFocus, onBlur}) => (
                <>
                    <LinedInput
                        ref={firstRef}
                        id={models.AppKeys.FirstName}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        name={models.AppKeys.FirstName}
                        label={strings.firstName}
                        type={InputType.Text}
                        value={props.firstName}
                        setValue={(value) => props.onChangeName(models.AppKeys.FirstName, value)}
                        required={true}
                        maxLength={30}
                        validationMessage={props.validationFirst}
                    />
                    <LinedInput
                        ref={middleRef}
                        onFocus={onFocus}
                        id={models.AppKeys.MiddleInitial}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        name={models.AppKeys.MiddleInitial}
                        label={strings.middleInitial}
                        type={InputType.Text}
                        value={props.middleInitial}
                        setValue={(value) => {
                            props.onChangeName(models.AppKeys.MiddleInitial, value);
                        }}
                        maxLength={1}
                        validationMessage={props.validationMiddle}
                    />
                    <LinedInput
                        ref={lastRef}
                        onFocus={onFocus}
                        id={models.AppKeys.LastName}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        name={models.AppKeys.LastName}
                        label={strings.lastName}
                        type={InputType.Text}
                        value={props.lastName}
                        setValue={(value) => props.onChangeName(models.AppKeys.LastName, value)}
                        required={true}
                        maxLength={30}
                        validationMessage={props.validationLast}
                    />
                </>
            )}
        </ExpandableInputGroup>
    );
}
